import { SETTINGS, BREAKPOINTS } from '../../utils/constants';
import initLoadMore from '../../components/load-more';
import initCustomSortBy from './initCustomSortBy';
import { syncAllExpandedFacetScrollSurfaces } from '../../utils/facetScrollStyles';
import { notifyVatRefresh } from '../../utils/vatDisplay';

const FILTERS_DRAWER_SCOPE_SELECTOR = '[data-filters-drawer-scope]';
const FILTER_PANEL_BODY_SELECTOR = '.collection-filters-panel__body';
const MOBILE_FILTERS_COUNT_SELECTOR = '[data-collection-mobile-filters-count]';
const FILTERED_PRODUCTS_COUNT_ATTR = 'data-collection-filtered-products-count';
const BANNER_PRODUCTS_COUNT_SELECTOR = '[data-collection-banner-products-count]';

const pendingByForm = new WeakMap();
const debounceByForm = new WeakMap();

const parseHtml = html => new DOMParser().parseFromString(html, 'text/html');

const getSectionId = form => {
  if (form?.dataset.sectionId) return form.dataset.sectionId;

  const scope = form?.closest(FILTERS_DRAWER_SCOPE_SELECTOR);
  if (scope?.dataset.sectionId) return scope.dataset.sectionId;

  const sectionEl =
    form?.closest('.shopify-section[id^="shopify-section-"]') ||
    scope?.closest('.shopify-section[id^="shopify-section-"]');

  return sectionEl?.id?.replace(/^shopify-section-/, '') || null;
};

const buildFilterParams = form => {
  const params = new URLSearchParams(new FormData(form));
  params.delete('section_id');
  params.delete('sections');

  const scope = form?.closest(FILTERS_DRAWER_SCOPE_SELECTOR);
  const isDesktopViewport = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`).matches;
  const desktopSortEl = scope?.querySelector('form.collection-sort select[name="sort_by"]');

  // Desktop PLP sort is a separate form; mobile drawer uses radios inside this form.
  if (isDesktopViewport && desktopSortEl?.value) {
    params.set('sort_by', desktopSortEl.value);
  } else if (!params.has('sort_by')) {
    const sortEl = scope?.querySelector('select[name="sort_by"]');
    if (sortEl?.value) {
      params.set('sort_by', sortEl.value);
    }
  }

  return params;
};

const buildFilterPath = form => {
  const action = form.getAttribute('action') || window.location.pathname;
  return action.startsWith('http') ? new URL(action).pathname : action;
};

const extractSectionHtml = (data, sectionId) => {
  if (!data || !sectionId) return null;
  if (typeof data === 'string') return data;
  if (data[sectionId]) return data[sectionId];
  if (data.sections?.[sectionId]) return data.sections[sectionId];
  const keys = Object.keys(data);
  if (keys.length === 1 && typeof data[keys[0]] === 'string') return data[keys[0]];
  return null;
};

const fetchCollectionSectionHtml = async (form, sectionId, signal, filterSourceUrl = null) => {
  let path;
  let filterQuery;
  if (filterSourceUrl) {
    const u = new URL(filterSourceUrl, window.location.origin);
    u.searchParams.delete('section_id');
    u.searchParams.delete('sections');
    path = u.pathname;
    filterQuery = u.searchParams.toString();
  } else {
    path = buildFilterPath(form);
    filterQuery = buildFilterParams(form).toString();
  }
  const filterUrl = filterQuery ? `${path}?${filterQuery}` : path;

  const sectionsUrl = new URL(filterUrl, window.location.origin);
  sectionsUrl.searchParams.set('sections', sectionId);

  let response = await fetch(sectionsUrl.toString(), {
    signal,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });

  if (response.ok) {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('json')) {
      const sectionHtml = extractSectionHtml(await response.json(), sectionId);
      if (sectionHtml) return sectionHtml;
    } else {
      return await response.text();
    }
  }

  const sectionUrl = new URL(filterUrl, window.location.origin);
  sectionUrl.searchParams.set('section_id', sectionId);
  response = await fetch(sectionUrl.toString(), {
    signal,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (response.ok) return await response.text();

  response = await fetch(filterUrl, { signal });
  if (!response.ok) return null;

  const doc = parseHtml(await response.text());
  const sectionEl =
    doc.getElementById(`shopify-section-${sectionId}`) ||
    doc.querySelector(`#shopify-section-${CSS.escape(sectionId)}`);

  return sectionEl?.innerHTML ?? null;
};

const parseCollectionSection = sectionHtml => {
  const doc = parseHtml(sectionHtml);
  return {
    form: doc.querySelector('[data-filter-form]'),
    plpMain: doc.querySelector('[data-collection-plp-main]'),
    scope: doc.querySelector(FILTERS_DRAWER_SCOPE_SELECTOR),
  };
};

const syncFilterCounts = (scope, form) => {
  if (!scope || !form) return;

  const sourceCount = form.querySelector('[data-collection-filters-active-count]');
  const mobileCount = scope.querySelector(MOBILE_FILTERS_COUNT_SELECTOR);
  if (sourceCount && mobileCount) {
    mobileCount.textContent = sourceCount.textContent;
  }
};

/** Mobile filter bar lives outside `<form>`; copy count from parsed section markup (same SSR as collection.liquid). */
const syncMobileFiltersCountFromParsedScope = (liveScope, parsedScope) => {
  if (!liveScope || !parsedScope) return;
  const next = parsedScope.querySelector(MOBILE_FILTERS_COUNT_SELECTOR);
  const cur = liveScope.querySelector(MOBILE_FILTERS_COUNT_SELECTOR);
  if (next && cur) {
    cur.textContent = next.textContent;
  }
};

const syncFilteredCountOnScope = (liveScope, parsedScope) => {
  if (!liveScope || !parsedScope) return;
  const next = parsedScope.getAttribute(FILTERED_PRODUCTS_COUNT_ATTR);
  if (next == null || next === '') return;
  liveScope.setAttribute(FILTERED_PRODUCTS_COUNT_ATTR, next);
};

/** Banner is a separate section; PLP facet fetch does not re-render it — mirror filtered `products_count`. */
const syncCollectionBannerProductCount = parsedScope => {
  const raw = parsedScope?.getAttribute(FILTERED_PRODUCTS_COUNT_ATTR);
  if (raw == null || raw === '') return;
  const label = `(${raw})`;
  document.querySelectorAll(BANNER_PRODUCTS_COUNT_SELECTOR).forEach(el => {
    el.textContent = label;
  });
};

const syncCollectionPlp = (scope, parsed, { preserveFilterScroll = true } = {}) => {
  if (!scope || !parsed) return false;

  const currentForm = scope.querySelector('[data-filter-form]');
  const { form: newForm, plpMain: newPlpMain } = parsed;
  if (!currentForm || !newForm) return false;

  const panelBody = currentForm.querySelector(FILTER_PANEL_BODY_SELECTOR);
  const scrollTop = preserveFilterScroll ? panelBody?.scrollTop ?? 0 : 0;

  currentForm.innerHTML = newForm.innerHTML;
  syncFilterCounts(scope, currentForm);
  syncMobileFiltersCountFromParsedScope(scope, parsed.scope);
  syncFilteredCountOnScope(scope, parsed.scope);
  syncCollectionBannerProductCount(parsed.scope);

  const nextPanelBody = currentForm.querySelector(FILTER_PANEL_BODY_SELECTOR);
  if (nextPanelBody && preserveFilterScroll) {
    nextPanelBody.scrollTop = scrollTop;
  }

  const currentPlpMain = scope.querySelector('[data-collection-plp-main]');
  if (currentPlpMain && newPlpMain) {
    currentPlpMain.innerHTML = newPlpMain.innerHTML;
    initCustomSortBy();
    initLoadMore();
    // Replaced cards are SSR'd in the default VAT mode; re-apply the visitor's toggle state.
    notifyVatRefresh();
  }

  syncAllExpandedFacetScrollSurfaces(currentForm);

  return true;
};

/**
 * Fetches the collection/search section for the current filter form state and
 * updates facet availability + PLP in place (no full page reload).
 * @param {HTMLFormElement} form
 * @param {{ filterSourceUrl?: string }} [options]
 */
export const refreshDeferredFacets = async (form, options = {}) => {
  const sectionId = getSectionId(form);
  if (!sectionId) return;

  const previous = pendingByForm.get(form);
  previous?.abort();
  const abortController = new AbortController();
  pendingByForm.set(form, abortController);

  const scope = form.closest(FILTERS_DRAWER_SCOPE_SELECTOR);
  const panelBody = form.querySelector(FILTER_PANEL_BODY_SELECTOR);

  form.setAttribute('aria-busy', 'true');
  panelBody?.classList.add('opacity-60', 'pointer-events-none');

  try {
    const sectionHtml = await fetchCollectionSectionHtml(
      form,
      sectionId,
      abortController.signal,
      options.filterSourceUrl ?? null,
    );
    if (!sectionHtml) return;

    const parsed = parseCollectionSection(sectionHtml);
    if (!syncCollectionPlp(scope, parsed)) return;

    let nextUrl;
    if (options.filterSourceUrl) {
      const u = new URL(options.filterSourceUrl, window.location.origin);
      u.searchParams.delete('section_id');
      u.searchParams.delete('sections');
      nextUrl = `${u.pathname}${u.searchParams.toString() ? `?${u.searchParams}` : ''}`;
    } else {
      const params = buildFilterParams(form);
      const path = buildFilterPath(form);
      nextUrl = params.toString() ? `${path}?${params}` : path;
    }
    window.history.replaceState({ collectionFilters: true }, '', nextUrl);
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.error('Failed to refresh collection facets', error);
    }
  } finally {
    if (pendingByForm.get(form) === abortController) {
      pendingByForm.delete(form);
    }
    form.removeAttribute('aria-busy');
    form.querySelector(FILTER_PANEL_BODY_SELECTOR)?.classList.remove('opacity-60', 'pointer-events-none');
  }
};

export const scheduleDeferredFacetRefresh = form => {
  if (!form) return;

  const existing = debounceByForm.get(form);
  if (existing) clearTimeout(existing);

  debounceByForm.set(
    form,
    setTimeout(() => {
      debounceByForm.delete(form);
      refreshDeferredFacets(form);
    }, SETTINGS.FILTER_UPDATE_DELAY)
  );
};
